import {
  CellStyle,
  ColDef,
  ColGroupDef,
  ValueGetterParams,
} from 'ag-grid-community'
import { intl } from '../../../../i18n'
import { UserBasic } from '../../../../lib/functions/user'
import DateVO from '../../../../vo/DateVO'
import { NumberCellRenderer } from '../../../containers/BulkSheetView/components/cellRenderer/NumberCellRenderer'
import {
  ColumnType,
  CURRENT_MONTH_BACKGROUND_COLOR,
} from '../../../containers/commons/AgGrid'
import { SequenceNoCellRenderer } from '../../../containers/commons/AgGrid/components/cell/custom/sequenceNo/SequenceNoCellRenderer'
import { ClientSideSelectFilter } from '../../../containers/BulkSheetView/components/filter'
import { getRowNumber } from '../../../containers/BulkSheetView/lib/gridApi'
import {
  MemberWorkloadStatusType,
  MemberWorkloadStatusRow,
} from '../MemberWorkloadStatus'
import {
  calculateWorkMonthDisplayValue,
  calculateTotalColumnValue,
} from './calculation'
import { isCurrentPeriod } from './periodUtils'

export interface WorkingDay {
  businessDays: number
  workHours: number
}

export interface WorkingDayCalendar {
  [yearMonth: string]: WorkingDay
}

export type WorkloadUnit = 'MONTH' | 'DAY' | 'HOUR'

const NUMBER_DECIMAL_POINT: number = 2

export const ALERT_UPPER_LIMIT_FOR_MAN_MONTH: number = 1.0
export const COLUMN_FIELD_TOTAL: string = 'total'
export const WORKMONTHS_FIELD_PREFIX: string = 'body.memberWorkMonths.'

export const ROW_NUMBER_COL_DEF: ColDef = {
  field: 'rowNumber',
  type: [ColumnType.sequenceNo],
  resizable: false,
  cellRenderer: SequenceNoCellRenderer,
  valueGetter: params => {
    return getRowNumber(params.node)
  },
}

export const INFORMATION_COLUMN_GROUP_DEF_BASE = {
  groupId: 'information',
  headerName: intl.formatMessage({
    id: 'memberWorkload.status.information',
  }),
}

export const MEMBER_COLUMN_GROUP_DEF_BASE = {
  groupId: 'member',
  headerName: intl.formatMessage({
    id: 'memberWorkload.status.member',
  }),
}

export const generateTotalColumnDef = (
  summaryTargetRowType: MemberWorkloadStatusType,
  getRowGroupColumnKey: (row: MemberWorkloadStatusRow) => string | undefined
): ColDef => {
  return {
    field: COLUMN_FIELD_TOTAL,
    headerName: intl.formatMessage({
      id: 'memberWorkload.status.total',
    }),
    width: 85,
    hide: false,
    pinned: true,
    cellRenderer: NumberCellRenderer,
    cellRendererParams: {
      numberWithCommas: true,
      decimalPoints: NUMBER_DECIMAL_POINT,
    },
    valueGetter: (params: ValueGetterParams) => {
      return calculateTotalColumnValue(params)
    },
    floatingFilter: true,
    filter: 'clientSideNumberFilter',
    cellClass: 'numberStyle',
    sortable: false,
  }
}

const userBasicComparator = (
  valueA: string | UserBasic,
  valueB: string | UserBasic
): number => {
  const strA: string = typeof valueA === 'string' ? valueA : valueA?.name || ''
  const strB: string = typeof valueB === 'string' ? valueB : valueB?.name || ''
  return strA.localeCompare(strB)
}

export const CHANGE_LOG_COLUMN_GROUP: ColGroupDef = {
  headerName: intl.formatMessage({ id: 'changeLog' }),
  children: [
    {
      field: 'revision',
      headerName: intl.formatMessage({ id: 'revision' }),
      hide: true,
      width: 90,
      floatingFilter: true,
      filter: 'clientSideTextFilter',
    },
    {
      field: 'createdBy',
      headerName: intl.formatMessage({ id: 'createdBy' }),
      hide: true,
      width: 150,
      valueGetter: (params: ValueGetterParams) => {
        return params.data.createdBy
      },
      type: [ColumnType.autocomplete],
      cellRendererParams: {
        uiMeta: {},
      },
      floatingFilter: true,
      filter: ClientSideSelectFilter,
      comparator: userBasicComparator,
    },
    {
      field: 'createdAt',
      headerName: intl.formatMessage({ id: 'createdAt' }),
      hide: true,
      width: 120,
      type: [ColumnType.dateTime],
      floatingFilter: true,
      cellClass: 'dateTimeStyle',
    },
    {
      field: 'updatedBy',
      headerName: intl.formatMessage({ id: 'updatedBy' }),
      hide: true,
      width: 150,
      valueGetter: (params: ValueGetterParams) => {
        return params.data.updatedBy
      },
      type: [ColumnType.autocomplete],
      cellRendererParams: {
        uiMeta: {},
      },
      floatingFilter: true,
      filter: ClientSideSelectFilter,
      comparator: userBasicComparator,
    },
    {
      field: 'updatedAt',
      headerName: intl.formatMessage({ id: 'updatedAt' }),
      hide: true,
      width: 120,
      type: [ColumnType.dateTime],
      floatingFilter: true,
      cellClass: 'dateTimeStyle',
    },
  ],
}

const createBaseCellStyle = (): CellStyle => ({
  justifyContent: 'flex-end',
  color: 'black',
})

const createCellStyleWithCurrentPeriodHighlight = (
  yearMonth: string
): CellStyle => {
  const style = createBaseCellStyle()
  if (isCurrentPeriod(yearMonth)) {
    style.backgroundColor = CURRENT_MONTH_BACKGROUND_COLOR
  }
  return style
}

export const generateWorkMonthColDef = (
  yearMonth: string,
  getWorkingTimeTotal: (yearMonth: string) => string,
  summaryTargetRowType: MemberWorkloadStatusType,
  getRowGroupColumnKey: (row: MemberWorkloadStatusRow) => string | undefined
): ColDef => {
  return {
    colId: yearMonth,
    field: yearMonth,
    headerName: getWorkingTimeTotal(yearMonth),
    width: 78,
    suppressMovable: true,
    valueGetter: (params: ValueGetterParams) => {
      return calculateWorkMonthDisplayValue(
        params,
        yearMonth,
        summaryTargetRowType,
        getRowGroupColumnKey
      )
    },
    cellRenderer: NumberCellRenderer,
    cellRendererParams: {
      numberWithCommas: true,
      decimalPoints: NUMBER_DECIMAL_POINT,
    },
    floatingFilter: true,
    filter: 'clientSideNumberFilter',
    cellStyle: params => {
      const currentYearMonth =
        params.colDef.colId || params.colDef.field || yearMonth
      return createCellStyleWithCurrentPeriodHighlight(currentYearMonth)
    },
    cellClass: 'numberStyle',
  }
}
